"use client";;
import { useDirection } from "@radix-ui/react-direction";
import { Slot } from "@radix-ui/react-slot";
import * as React from "react";
import { useComposedRefs } from "@/lib/compose-refs";
import { cn } from "@/lib/utils";
import { VisuallyHiddenInput } from "@/components/visually-hidden-input";
import { useAsRef } from "@/hooks/use-as-ref";
import { useIsomorphicLayoutEffect } from "@/hooks/use-isomorphic-layout-effect";
import { useLazyRef } from "@/hooks/use-lazy-ref";

const ROOT_NAME = "Editable";
const LABEL_NAME = "EditableLabel";
const AREA_NAME = "EditableArea";
const PREVIEW_NAME = "EditablePreview";
const INPUT_NAME = "EditableInput";
const TRIGGER_NAME = "EditableTrigger";
const TOOLBAR_NAME = "EditableToolbar";
const CANCEL_NAME = "EditableCancel";
const SUBMIT_NAME = "EditableSubmit";

const StoreContext = React.createContext(null);

function useStoreContext(consumerName) {
  const context = React.useContext(StoreContext);
  if (!context) {
    throw new Error(`\`${consumerName}\` must be used within \`${ROOT_NAME}\``);
  }
  return context;
}

function useStore(selector, ogStore) {
  const contextStore = React.useContext(StoreContext);

  const store = ogStore ?? contextStore;

  if (!store) {
    throw new Error(`\`useStore\` must be used within \`${ROOT_NAME}\``);
  }

  const getSnapshot = React.useCallback(() => selector(store.getState()), [store, selector]);

  return React.useSyncExternalStore(store.subscribe, getSnapshot, getSnapshot);
}

const EditableContext = React.createContext(null);

function useEditableContext(consumerName) {
  const context = React.useContext(EditableContext);
  if (!context) {
    throw new Error(`\`${consumerName}\` must be used within \`${ROOT_NAME}\``);
  }
  return context;
}

function Editable(props) {
  const {
    value: valueProp,
    defaultValue = "",
    defaultEditing,
    editing: editingProp,
    onValueChange,
    onEditingChange,
    onCancel: onCancelProp,
    onEdit: onEditProp,
    onSubmit: onSubmitProp,
    onEscapeKeyDown,
    onEnterKeyDown,
    dir: dirProp,
    maxLength,
    name,
    placeholder,
    triggerMode = "click",
    asChild,
    autosize = false,
    disabled,
    required,
    readOnly,
    invalid,
    className,
    id,
    ref,
    ...rootProps
  } = props;

  const instanceId = React.useId();
  const rootId = id ?? instanceId;

  const inputId = React.useId();
  const labelId = React.useId();

  const dir = useDirection(dirProp);

  const previousValueRef = React.useRef(defaultValue);

  const [formTrigger, setFormTrigger] = React.useState(null);
  const composedRef = useComposedRefs(ref, (node) => setFormTrigger(node));
  const isFormControl = formTrigger ? !!formTrigger.closest("form") : true;

  const listenersRef = useLazyRef(() => new Set());
  const stateRef = useLazyRef(() => ({
    value: valueProp ?? defaultValue,
    editing: editingProp ?? defaultEditing ?? false,
    cursorPosition: null, // null means "end of text", number means specific position
  }));

  const propsRef = useAsRef({
    onValueChange,
    onEditingChange,
    onCancel: onCancelProp,
    onEdit: onEditProp,
    onSubmit: onSubmitProp,
    onEscapeKeyDown,
    onEnterKeyDown,
  });

  const store = React.useMemo(() => {
    return {
      subscribe: (cb) => {
        listenersRef.current.add(cb);
        return () => listenersRef.current.delete(cb);
      },
      getState: () => stateRef.current,
      setState: (key, value) => {
        if (Object.is(stateRef.current[key], value)) return;

        if (key === "value" && typeof value === "string") {
          stateRef.current.value = value;
          propsRef.current.onValueChange?.(value);
        } else if (key === "editing" && typeof value === "boolean") {
          stateRef.current.editing = value;
          propsRef.current.onEditingChange?.(value);
        } else {
          stateRef.current[key] = value;
        }

        store.notify();
      },
      notify: () => {
        for (const cb of listenersRef.current) {
          cb();
        }
      },
    };
  }, [listenersRef, stateRef, propsRef]);

  const value = useStore((state) => state.value, store);

  useIsomorphicLayoutEffect(() => {
    if (valueProp !== undefined) {
      store.setState("value", valueProp);
    }
  }, [valueProp]);

  useIsomorphicLayoutEffect(() => {
    if (editingProp !== undefined) {
      store.setState("editing", editingProp);
    }
  }, [editingProp]);

  const onCancel = React.useCallback(() => {
    const prevValue = previousValueRef.current;
    store.setState("value", prevValue);
    store.setState("editing", false);
    propsRef.current.onCancel?.();
  }, [store, propsRef]);

  const onEdit = React.useCallback(() => {
    const currentValue = store.getState().value;
    previousValueRef.current = currentValue;
    store.setState("editing", true);
    propsRef.current.onEdit?.();
  }, [store, propsRef]);

  const onSubmit = React.useCallback((newValue) => {
    store.setState("value", newValue);
    store.setState("editing", false);
    propsRef.current.onSubmit?.(newValue);
  }, [store, propsRef]);

  const contextValue = React.useMemo(() => ({
    rootId,
    inputId,
    labelId,
    defaultValue,
    onSubmit,
    onEdit,
    onCancel,
    onEscapeKeyDown,
    onEnterKeyDown,
    dir,
    maxLength,
    placeholder,
    triggerMode,
    autosize,
    disabled,
    readOnly,
    required,
    invalid,
  }), [
    rootId,
    inputId,
    labelId,
    defaultValue,
    onSubmit,
    onCancel,
    onEdit,
    onEscapeKeyDown,
    onEnterKeyDown,
    dir,
    maxLength,
    placeholder,
    triggerMode,
    autosize,
    disabled,
    required,
    readOnly,
    invalid,
  ]);

  const RootPrimitive = asChild ? Slot : "div";

  return (
    <StoreContext.Provider value={store}>
      <EditableContext.Provider value={contextValue}>
        <RootPrimitive
          data-slot="editable"
          {...rootProps}
          id={id}
          ref={composedRef}
          className={cn("flex min-w-0 flex-col gap-2", className)} />
        {isFormControl && (
          <VisuallyHiddenInput
            type="hidden"
            control={formTrigger}
            name={name}
            value={value}
            disabled={disabled}
            readOnly={readOnly}
            required={required} />
        )}
      </EditableContext.Provider>
    </StoreContext.Provider>
  );
}

function EditableLabel(props) {
  const { asChild, className, children, ref, ...labelProps } = props;
  const context = useEditableContext(LABEL_NAME);

  const LabelPrimitive = asChild ? Slot : "label";

  return (
    <LabelPrimitive
      data-disabled={context.disabled ? "" : undefined}
      data-invalid={context.invalid ? "" : undefined}
      data-required={context.required ? "" : undefined}
      data-slot="editable-label"
      {...labelProps}
      ref={ref}
      id={context.labelId}
      htmlFor={context.inputId}
      className={cn(
        "font-medium text-sm leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 data-required:after:ml-0.5 data-required:after:text-destructive data-required:after:content-['*']",
        className
      )}>
      {children}
    </LabelPrimitive>
  );
}

function EditableArea(props) {
  const { asChild, className, ref, ...areaProps } = props;
  const context = useEditableContext(AREA_NAME);
  const editing = useStore((state) => state.editing);

  const AreaPrimitive = asChild ? Slot : "div";

  return (
    <AreaPrimitive
      role="group"
      data-disabled={context.disabled ? "" : undefined}
      data-editing={editing ? "" : undefined}
      data-slot="editable-area"
      dir={context.dir}
      {...areaProps}
      ref={ref}
      className={cn(
        "relative inline-grid min-w-0 data-disabled:cursor-not-allowed data-disabled:opacity-50 [&>*]:col-start-1 [&>*]:row-start-1",
        className
      )} />
  );
}

function EditablePreview(props) {
  const {
    onClick: onClickProp,
    onDoubleClick: onDoubleClickProp,
    onFocus: onFocusProp,
    onKeyDown: onKeyDownProp,
    asChild,
    className,
    ref,
    ...previewProps
  } = props;

  const context = useEditableContext(PREVIEW_NAME);
  const store = useStoreContext(PREVIEW_NAME);
  const value = useStore((state) => state.value);
  const editing = useStore((state) => state.editing);
  const previewRef = React.useRef(null);
  const composedRef = useComposedRefs(ref, previewRef);

  const propsRef = useAsRef({
    onClick: onClickProp,
    onDoubleClick: onDoubleClickProp,
    onFocus: onFocusProp,
    onKeyDown: onKeyDownProp,
  });

  const getCursorPositionFromClick = React.useCallback((event) => {
    if (!previewRef.current || !value) return value?.length ?? 0;

    const element = previewRef.current;
    const rect = element.getBoundingClientRect();
    const relativeX = event.clientX - rect.left;

    // Get computed font style for accurate measurement
    const computedStyle = window.getComputedStyle(element);
    const font = `${computedStyle.fontWeight} ${computedStyle.fontSize} ${computedStyle.fontFamily}`;

    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");
    ctx.font = font;

    // Find the character position closest to the click
    for (let i = 0; i <= value.length; i++) {
      const width = ctx.measureText(value.substring(0, i)).width;
      if (width >= relativeX) {
        // Check if we're closer to this position or the previous one
        if (i > 0) {
          const prevWidth = ctx.measureText(value.substring(0, i - 1)).width;
          if (relativeX - prevWidth < width - relativeX) {
            return i - 1;
          }
        }
        return i;
      }
    }
    return value.length;
  }, [value]);

  const onTrigger = React.useCallback((cursorPosition = null) => {
    if (context.disabled || context.readOnly) return;
    store.setState("cursorPosition", cursorPosition);
    context.onEdit();
  }, [context.onEdit, context.disabled, context.readOnly, store]);

  const onClick = React.useCallback((event) => {
    propsRef.current.onClick?.(event);
    if (event.defaultPrevented || context.triggerMode !== "click") return;

    const cursorPosition = getCursorPositionFromClick(event);
    onTrigger(cursorPosition);
  }, [propsRef, onTrigger, context.triggerMode, getCursorPositionFromClick]);

  const onDoubleClick = React.useCallback((event) => {
    propsRef.current.onDoubleClick?.(event);
    if (event.defaultPrevented || context.triggerMode !== "dblclick") return;

    const cursorPosition = getCursorPositionFromClick(event);
    onTrigger(cursorPosition);
  }, [propsRef, onTrigger, context.triggerMode, getCursorPositionFromClick]);

  const onFocus = React.useCallback((event) => {
    propsRef.current.onFocus?.(event);
    if (event.defaultPrevented || context.triggerMode !== "focus") return;

    onTrigger();
  }, [propsRef, onTrigger, context.triggerMode]);

  const onKeyDown = React.useCallback((event) => {
    propsRef.current.onKeyDown?.(event);
    if (event.defaultPrevented) return;

    if (event.key === "Enter") {
      const nativeEvent = event.nativeEvent;
      if (context.onEnterKeyDown) {
        context.onEnterKeyDown(nativeEvent);
        if (nativeEvent.defaultPrevented) return;
      }
      onTrigger();
    }
  }, [propsRef, onTrigger, context.onEnterKeyDown]);

  const PreviewPrimitive = asChild ? Slot : "div";

  if (context.readOnly) return null;

  return (
    <PreviewPrimitive
      role="button"
      aria-disabled={context.disabled || context.readOnly}
      aria-hidden={editing ? true : undefined}
      data-empty={!value ? "" : undefined}
      data-disabled={context.disabled ? "" : undefined}
      data-readonly={context.readOnly ? "" : undefined}
      data-slot="editable-preview"
      tabIndex={context.disabled || context.readOnly || editing ? undefined : 0}
      {...previewProps}
      ref={composedRef}
      onClick={editing ? undefined : onClick}
      onDoubleClick={editing ? undefined : onDoubleClick}
      onFocus={editing ? undefined : onFocus}
      onKeyDown={editing ? undefined : onKeyDown}
      className={cn(
        "cursor-text truncate rounded-sm focus-visible:outline-hidden focus-visible:ring-1 focus-visible:ring-ring data-disabled:cursor-not-allowed data-readonly:cursor-default data-empty:text-muted-foreground data-disabled:opacity-50",
        editing && "invisible",
        className
      )}>
      {value || context.placeholder}
    </PreviewPrimitive>
  );
}

function EditableInput(props) {
  const {
    onBlur: onBlurProp,
    onChange: onChangeProp,
    onKeyDown: onKeyDownProp,
    asChild,
    className,
    disabled,
    readOnly,
    required,
    maxLength,
    ref,
    ...inputProps
  } = props;

  const context = useEditableContext(INPUT_NAME);
  const store = useStoreContext(INPUT_NAME);
  const value = useStore((state) => state.value);
  const editing = useStore((state) => state.editing);
  const inputRef = React.useRef(null);
  const composedRef = useComposedRefs(ref, inputRef);

  const propsRef = useAsRef({
    onBlur: onBlurProp,
    onChange: onChangeProp,
    onKeyDown: onKeyDownProp,
  });

  const isDisabled = disabled || context.disabled;
  const isReadOnly = readOnly || context.readOnly;
  const isRequired = required || context.required;

  const onAutosize = React.useCallback((target) => {
    if (!context.autosize) return;

    if (target instanceof HTMLTextAreaElement) {
      target.style.height = "0";
      target.style.height = `${target.scrollHeight}px`;
    } else {
      target.style.width = "0";
      target.style.width = `${target.scrollWidth + 4}px`;
    }
  }, [context.autosize]);

  const onBlur = React.useCallback((event) => {
    if (isDisabled || isReadOnly) return;

    propsRef.current.onBlur?.(event);
    if (event.defaultPrevented) return;

    const relatedTarget = event.relatedTarget;

    const isAction =
      relatedTarget instanceof HTMLElement &&
      (relatedTarget.closest(`[data-slot="editable-trigger"]`) ||
        relatedTarget.closest(`[data-slot="editable-cancel"]`));

    if (!isAction) {
      context.onSubmit(value);
    }
  }, [value, context.onSubmit, propsRef, isDisabled, isReadOnly]);

  const onChange = React.useCallback((event) => {
    if (isDisabled || isReadOnly) return;

    propsRef.current.onChange?.(event);
    if (event.defaultPrevented) return;

    store.setState("value", event.target.value);
    onAutosize(event.target);
  }, [store, propsRef, onAutosize, isDisabled, isReadOnly]);

  const onKeyDown = React.useCallback((event) => {
    if (isDisabled || isReadOnly) return;

    propsRef.current.onKeyDown?.(event);
    if (event.defaultPrevented) return;

    if (event.key === "Escape") {
      const nativeEvent = event.nativeEvent;
      if (context.onEscapeKeyDown) {
        context.onEscapeKeyDown(nativeEvent);
        if (nativeEvent.defaultPrevented) return;
      }
      context.onCancel();
    } else if (event.key === "Enter") {
      context.onSubmit(value);
    }
  }, [
    value,
    context.onSubmit,
    context.onCancel,
    context.onEscapeKeyDown,
    propsRef,
    isDisabled,
    isReadOnly,
  ]);

  useIsomorphicLayoutEffect(() => {
    if (!editing || isDisabled || isReadOnly || !inputRef.current) return;

    const frameId = window.requestAnimationFrame(() => {
      if (!inputRef.current) return;

      inputRef.current.focus();
      // Use stored cursor position if available, otherwise move to end
      const cursorPosition = store.getState().cursorPosition;
      const position = cursorPosition !== null ? cursorPosition : inputRef.current.value.length;
      inputRef.current.setSelectionRange(position, position);
      // Reset cursor position after using it
      store.setState("cursorPosition", null);
      onAutosize(inputRef.current);
    });

    return () => {
      window.cancelAnimationFrame(frameId);
    };
  }, [editing, onAutosize, isDisabled, isReadOnly, store]);

  const InputPrimitive = asChild ? Slot : "input";

  if (!editing && !isReadOnly) return null;

  return (
    <InputPrimitive
      aria-required={isRequired}
      aria-invalid={context.invalid}
      data-slot="editable-input"
      dir={context.dir}
      disabled={isDisabled}
      readOnly={isReadOnly}
      required={isRequired}
      {...inputProps}
      id={context.inputId}
      aria-labelledby={context.labelId}
      ref={composedRef}
      maxLength={maxLength}
      placeholder={context.placeholder}
      value={value}
      onBlur={onBlur}
      onChange={onChange}
      onKeyDown={onKeyDown}
      className={cn(
        "flex transition-colors placeholder:text-muted-foreground focus-visible:outline-hidden disabled:cursor-not-allowed disabled:opacity-50",
        context.autosize ? "w-auto" : "w-full",
        className
      )} />
  );
}

function EditableTrigger(props) {
  const { asChild, forceMount = false, ref, ...triggerProps } = props;
  const context = useEditableContext(TRIGGER_NAME);
  const editing = useStore((state) => state.editing);

  const onTrigger = React.useCallback(() => {
    if (context.disabled || context.readOnly) return;
    context.onEdit();
  }, [context.disabled, context.readOnly, context.onEdit]);

  const TriggerPrimitive = asChild ? Slot : "button";

  if (!forceMount && (editing || context.readOnly)) return null;

  return (
    <TriggerPrimitive
      type="button"
      aria-controls={context.rootId}
      aria-disabled={context.disabled || context.readOnly}
      data-disabled={context.disabled ? "" : undefined}
      data-readonly={context.readOnly ? "" : undefined}
      data-slot="editable-trigger"
      {...triggerProps}
      ref={ref}
      onClick={context.triggerMode === "click" ? onTrigger : undefined}
      onDoubleClick={context.triggerMode === "dblclick" ? onTrigger : undefined} />
  );
}

function EditableToolbar(props) {
  const {
    asChild,
    className,
    orientation = "horizontal",
    ref,
    ...toolbarProps
  } = props;
  const context = useEditableContext(TOOLBAR_NAME);

  const ToolbarPrimitive = asChild ? Slot : "div";

  return (
    <ToolbarPrimitive
      role="toolbar"
      aria-controls={context.rootId}
      aria-orientation={orientation}
      data-slot="editable-toolbar"
      dir={context.dir}
      {...toolbarProps}
      ref={ref}
      className={cn(
        "flex items-center gap-2",
        orientation === "vertical" && "flex-col",
        className
      )} />
  );
}

function EditableCancel(props) {
  const { onClick: onClickProp, asChild, ref, ...cancelProps } = props;
  const context = useEditableContext(CANCEL_NAME);
  const editing = useStore((state) => state.editing);

  const propsRef = useAsRef({
    onClick: onClickProp,
  });

  const onClick = React.useCallback((event) => {
    if (context.disabled || context.readOnly) return;

    propsRef.current.onClick?.(event);
    if (event.defaultPrevented) return;

    context.onCancel();
  }, [propsRef, context.onCancel, context.disabled, context.readOnly]);

  const CancelPrimitive = asChild ? Slot : "button";

  if (!editing && !context.readOnly) return null;

  return (
    <CancelPrimitive
      type="button"
      aria-controls={context.rootId}
      data-slot="editable-cancel"
      {...cancelProps}
      onClick={onClick}
      ref={ref} />
  );
}

function EditableSubmit(props) {
  const { onClick: onClickProp, asChild, ref, ...submitProps } = props;
  const context = useEditableContext(SUBMIT_NAME);
  const value = useStore((state) => state.value);
  const editing = useStore((state) => state.editing);

  const propsRef = useAsRef({
    onClick: onClickProp,
  });

  const onClick = React.useCallback((event) => {
    if (context.disabled || context.readOnly) return;

    propsRef.current.onClick?.(event);
    if (event.defaultPrevented) return;

    context.onSubmit(value);
  }, [propsRef, context.onSubmit, value, context.disabled, context.readOnly]);

  const SubmitPrimitive = asChild ? Slot : "button";

  if (!editing && !context.readOnly) return null;

  return (
    <SubmitPrimitive
      type="button"
      aria-controls={context.rootId}
      data-slot="editable-submit"
      {...submitProps}
      ref={ref}
      onClick={onClick} />
  );
}

export { Editable, EditableLabel, EditableArea, EditablePreview, EditableInput, EditableTrigger, EditableToolbar, EditableCancel, EditableSubmit, //
useStore as useEditable };
